// client/src/pages/AttorneyWaitlist.tsx
// Specialty and expert type lists match ExpertIntake.tsx EXACTLY

import { useState } from 'react';
import { supabase } from '../supabase';
import StateSelect from '../components/StateSelect';
import Logo from '../components/Logo';

// ─── EXACT MATCH from ExpertIntake.tsx ────────────────────────
const MEDICAL_SPECIALTIES = [
    { value: 'anesthesiology', label: 'Anesthesiology', subSpecialties: ['Pain Management', 'Critical Care Anesthesiology', 'Pediatric Anesthesiology', 'Obstetric Anesthesiology'] },
    { value: 'cardiology', label: 'Cardiology', subSpecialties: ['Interventional Cardiology', 'Electrophysiology', 'Heart Failure', 'Pediatric Cardiology'] },
    { value: 'dermatology', label: 'Dermatology', subSpecialties: ['Mohs Surgery', 'Pediatric Dermatology', 'Cosmetic Dermatology', 'Immunodermatology'] },
    { value: 'emergency_medicine', label: 'Emergency Medicine', subSpecialties: ['Pediatric Emergency Medicine', 'Sports Medicine', 'Medical Toxicology', 'Wilderness Medicine'] },
    { value: 'endocrinology', label: 'Endocrinology', subSpecialties: ['Diabetes', 'Thyroid Disorders', 'Metabolic Bone Disease', 'Reproductive Endocrinology'] },
    { value: 'family_medicine', label: 'Family Medicine', subSpecialties: ['Sports Medicine', 'Geriatric Medicine', 'Adolescent Medicine', 'Hospice and Palliative Medicine'] },
    { value: 'gastroenterology', label: 'Gastroenterology', subSpecialties: ['Hepatology', 'Inflammatory Bowel Disease', 'Pancreatology', 'Motility Disorders'] },
    { value: 'internal_medicine', label: 'Internal Medicine', subSpecialties: ['Hospital Medicine', 'Geriatric Medicine', 'Sports Medicine', 'Clinical Informatics'] },
    { value: 'neurology', label: 'Neurology', subSpecialties: ['Stroke Neurology', 'Epilepsy', 'Movement Disorders', 'Neuromuscular Medicine'] },
    { value: 'obstetrics_gynecology', label: 'Obstetrics & Gynecology', subSpecialties: ['Maternal-Fetal Medicine', 'Reproductive Endocrinology', 'Gynecologic Oncology', 'Urogynecology'] },
    { value: 'oncology', label: 'Oncology', subSpecialties: ['Medical Oncology', 'Radiation Oncology', 'Pediatric Oncology', 'Surgical Oncology'] },
    { value: 'orthopedics', label: 'Orthopedics', subSpecialties: ['Spine Surgery', 'Joint Replacement', 'Sports Medicine', 'Pediatric Orthopedics'] },
    { value: 'pediatrics', label: 'Pediatrics', subSpecialties: ['Pediatric Neurology', 'Pediatric Cardiology', 'Neonatology', 'Pediatric Emergency Medicine'] },
    { value: 'psychiatry', label: 'Psychiatry', subSpecialties: ['Forensic Psychiatry', 'Child Psychiatry', 'Addiction Psychiatry', 'Geriatric Psychiatry'] },
    { value: 'radiology', label: 'Radiology', subSpecialties: ['Neuroradiology', 'Interventional Radiology', 'Pediatric Radiology', 'Nuclear Radiology'] },
    { value: 'surgery', label: 'Surgery', subSpecialties: ['Trauma Surgery', 'Vascular Surgery', 'Plastic Surgery', 'Pediatric Surgery'] },
    { value: 'urology', label: 'Urology', subSpecialties: ['Pediatric Urology', 'Urologic Oncology', 'Female Urology', 'Male Infertility'] },
];

const NURSING_SPECIALTIES = {
    value: 'nursing', label: 'Nursing', subSpecialties: [
        'Critical Care Nursing', 'Emergency Room Nursing', 'Perioperative/OR Nursing',
        'Medical-Surgical Nursing', 'Pediatric Nursing', 'Neonatal/NICU Nursing',
        'Obstetric/Labor & Delivery', 'Psychiatric/Mental Health Nursing', 'Geriatric Nursing',
        'Oncology Nursing', 'Cardiac/Telemetry Nursing', 'Rehabilitation Nursing',
        'Home Health Nursing', 'School Nursing', 'Infection Control', 'Wound Care Specialist',
        'Pain Management Nursing', 'Dialysis Nursing', 'Hospice/Palliative Care',
        'Case Management', 'Nurse Educator', 'Nurse Administrator', 'Legal Nurse Consultant',
        'Forensic Nursing', 'Flight/Transport Nursing', 'Occupational Health Nursing',
        'Public Health Nursing', 'Nurse Practitioner - Family', 'Nurse Practitioner - Adult',
        'Nurse Practitioner - Pediatric', 'Nurse Practitioner - Psychiatric',
        'Clinical Nurse Specialist', 'Certified Nurse Midwife',
        'Certified Registered Nurse Anesthetist'
    ]
};

const THERAPY_SPECIALTIES = {
    value: 'therapy', label: 'Therapy', subSpecialties: [
        'Physical Therapy', 'Occupational Therapy', 'Speech Therapy',
        'Respiratory Therapy', 'Massage Therapy', 'Behavioral Therapy'
    ]
};

const ALL_SPECIALTIES = [...MEDICAL_SPECIALTIES, NURSING_SPECIALTIES, THERAPY_SPECIALTIES];

// Expert types — exact match from ExpertIntake.tsx
const EXPERT_TYPES = [
    { value: 'physician', label: 'Physician' },
    { value: 'nurse', label: 'Nurse' },
    { value: 'therapist', label: 'Therapist' },
];

const CASE_TYPES = [
    'Medical Malpractice', 'Personal Injury', 'Workers\' Compensation',
    'Product Liability', 'Wrongful Death', 'Disability', 'Insurance Disputes',
    'Toxic Tort', 'Birth Injury', 'Nursing Home Negligence'
];

export default function AttorneyWaitList() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        firm_name: '',
        state: [] as string[],
        specialties_needed: [] as string[],
        sub_specialties_needed: [] as string[],
        expert_types_needed: [] as string[],
        case_types: [] as string[],
        case_description: '',
        urgency: 'no_rush',
        estimated_cases_per_year: '',
        budget_range: '',
        additional_notes: ''
    });

    const [expandedSpecialty, setExpandedSpecialty] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const toggleSpecialty = (value: string) => {
        setFormData(prev => {
            const isSelected = prev.specialties_needed.includes(value);
            const spec = ALL_SPECIALTIES.find(s => s.value === value);
            let newSubs = prev.sub_specialties_needed;
            if (isSelected && spec) {
                newSubs = newSubs.filter(s => !spec.subSpecialties.includes(s));
            }
            return {
                ...prev,
                specialties_needed: isSelected
                    ? prev.specialties_needed.filter(v => v !== value)
                    : [...prev.specialties_needed, value],
                sub_specialties_needed: newSubs
            };
        });
    };

    const toggleSubSpecialty = (value: string) => {
        setFormData(prev => ({
            ...prev,
            sub_specialties_needed: prev.sub_specialties_needed.includes(value)
                ? prev.sub_specialties_needed.filter(v => v !== value)
                : [...prev.sub_specialties_needed, value]
        }));
    };

    const toggleArrayField = (field: 'expert_types_needed' | 'case_types', value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: (prev[field] as string[]).includes(value)
                ? (prev[field] as string[]).filter(v => v !== value)
                : [...(prev[field] as string[]), value]
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!formData.name || !formData.email || formData.state.length === 0) {
            setError('Please fill in your name, email, and state.');
            setLoading(false);
            return;
        }

        if (formData.specialties_needed.length === 0) {
            setError('Please select at least one specialty you need.');
            setLoading(false);
            return;
        }

        try {
            const { error: dbError } = await supabase
                .from('attorney_waitlist')
                .insert({
                    name: formData.name.trim(),
                    email: formData.email.trim().toLowerCase(),
                    phone: formData.phone.trim() || null,
                    firm_name: formData.firm_name.trim() || null,
                    state: formData.state[0],
                    specialties_needed: formData.specialties_needed,
                    sub_specialties_needed: formData.sub_specialties_needed,
                    expert_types_needed: formData.expert_types_needed,
                    case_types: formData.case_types,
                    case_description: formData.case_description.trim() || null,
                    urgency: formData.urgency,
                    estimated_cases_per_year: formData.estimated_cases_per_year
                        ? parseInt(formData.estimated_cases_per_year)
                        : null,
                    budget_range: formData.budget_range || null,
                    additional_notes: formData.additional_notes.trim() || null
                });

            if (dbError) {
                if (dbError.code === '23505') {
                    setError('This email is already on our waitlist. We\'ll reach out soon!');
                } else {
                    setError('Something went wrong. Please try again.');
                    console.error('Waitlist insert error:', dbError);
                }
                setLoading(false);
                return;
            }
            // Send waitlist confirmation email
            try {
                const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8001').trim();
                await fetch(`${apiUrl}/notify/waitlist`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: formData.name,
                        email: formData.email
                    })
                });
            } catch (e) {}

            setSubmitted(true);
        } catch (err) {
            setError('Network error. Please check your connection and try again.');
            console.error('Waitlist submit error:', err);
        } finally {
            setLoading(false);
        }
    };

    // ─── SUCCESS STATE ───────────────────────────────────────────
    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4"
                 style={{ background: 'linear-gradient(135deg, #0a1628 0%, #132a4a 50%, #0a1628 100%)' }}>
                <div className="max-w-lg w-full text-center">
                    <div className="bg-white rounded-2xl shadow-2xl p-10">
                        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            You're on the List!
                        </h2>
                        <p className="text-gray-600 mb-6">
                            Thank you for your interest in Expert Link Pro. We're actively building our network
                            of verified medical experts based on demand from attorneys like you.
                            We'll notify you as soon as experts matching your needs are available.
                        </p>
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800">
                            <strong>What happens next:</strong> Our team reviews your requirements and
                            prioritizes recruiting experts in the specialties you need. Founding
                            attorneys get early access and preferred rates.
                        </div>
                        <a href="/"
                           className="inline-block mt-6 text-sm font-medium text-emerald-700 hover:text-emerald-800 underline">
                            ← Back to Homepage
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    // ─── FORM ────────────────────────────────────────────────────
    return (
        <div className="min-h-screen"
             style={{ background: 'linear-gradient(135deg, #0a1628 0%, #132a4a 50%, #0a1628 100%)' }}>

            {/* Header */}
            <div className="pt-8 pb-4 text-center">
                <a href="/" className="inline-block">
                    <Logo />
                </a>
            </div>

            {/* Hero Section */}
            <div className="max-w-3xl mx-auto px-4 pt-4 pb-8 text-center">
                <div className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase mb-4"
                     style={{ background: 'rgba(212, 175, 55, 0.15)', color: '#d4af37', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
                    Early Access — Founding Attorney Program
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                    Join the Waitlist
                </h1>
                <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                    Tell us what medical experts you need. We'll recruit them and give you
                    <span className="text-emerald-400 font-semibold"> priority access </span>
                    when they're on the platform.
                </p>
            </div>

            {/* Form Card */}
            <div className="max-w-3xl mx-auto px-4 pb-16">
                <form onSubmit={handleSubmit}
                      className="bg-white rounded-2xl shadow-2xl overflow-hidden">

                    {/* ── Section: Contact Info ────────────────── */}
                    <div className="p-6 md:p-8 border-b border-gray-100">
                        <h2 className="text-lg font-bold text-gray-900 mb-1">Your Information</h2>
                        <p className="text-sm text-gray-500 mb-5">So we can reach you when experts are ready.</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                                    placeholder="Jane Smith"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                                    placeholder="jane@lawfirm.com"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                                    placeholder="(555) 123-4567"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Firm Name</label>
                                <input
                                    type="text"
                                    value={formData.firm_name}
                                    onChange={e => setFormData({ ...formData, firm_name: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                                    placeholder="Smith & Associates"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    State <span className="text-red-500">*</span>
                                </label>
                                <StateSelect
                                    value={formData.state}
                                    onChange={(states: string[]) => setFormData({ ...formData, state: states })}
                                    required={true}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Section: Expert Type ─────────────────── */}
                    <div className="p-6 md:p-8 border-b border-gray-100">
                        <h2 className="text-lg font-bold text-gray-900 mb-1">
                            What Type of Expert Do You Need?
                        </h2>
                        <p className="text-sm text-gray-500 mb-5">Select all that apply.</p>

                        <div className="flex flex-wrap gap-3">
                            {EXPERT_TYPES.map(type => (
                                <button
                                    key={type.value}
                                    type="button"
                                    onClick={() => toggleArrayField('expert_types_needed', type.value)}
                                    className={`px-5 py-2.5 rounded-full text-sm font-medium border-2 transition
                                        ${formData.expert_types_needed.includes(type.value)
                                        ? 'bg-emerald-600 text-white border-emerald-600'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'}`}
                                >
                                    {type.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ── Section: Specialties with Sub-Specialties ── */}
                    <div className="p-6 md:p-8 border-b border-gray-100">
                        <h2 className="text-lg font-bold text-gray-900 mb-1">
                            What Specialties Do You Need?
                        </h2>
                        <p className="text-sm text-gray-500 mb-5">
                            Select specialties, then optionally pick specific sub-specialties.
                            This directly drives which experts we recruit first.
                            <span className="text-red-500"> *</span>
                        </p>

                        <div className="space-y-2 max-h-[500px] overflow-y-auto border border-gray-200 rounded-lg p-3">
                            {ALL_SPECIALTIES.map(spec => {
                                const isSelected = formData.specialties_needed.includes(spec.value);
                                const isExpanded = expandedSpecialty === spec.value;
                                const selectedSubCount = spec.subSpecialties.filter(
                                    s => formData.sub_specialties_needed.includes(s)
                                ).length;

                                return (
                                    <div key={spec.value} className="border border-gray-100 rounded-lg overflow-hidden">
                                        {/* Specialty Row */}
                                        <div className={`flex items-center justify-between px-4 py-3 cursor-pointer transition
                                            ${isSelected ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}
                                             onClick={() => toggleSpecialty(spec.value)}
                                        >
                                            <label className="flex items-center gap-3 cursor-pointer flex-1">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => {}}
                                                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                                                />
                                                <span className={`text-sm font-medium ${isSelected ? 'text-emerald-800' : 'text-gray-700'}`}>
                                                    {spec.label}
                                                </span>
                                                {selectedSubCount > 0 && (
                                                    <span className="text-xs bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full">
                                                        {selectedSubCount} sub
                                                    </span>
                                                )}
                                            </label>
                                            {isSelected && spec.subSpecialties.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedSpecialty(isExpanded ? null : spec.value);
                                                    }}
                                                    className="text-xs text-emerald-600 hover:text-emerald-800 font-medium px-2 py-1"
                                                >
                                                    {isExpanded ? '▲ Hide' : '▼ Sub-specialties'}
                                                </button>
                                            )}
                                        </div>

                                        {/* Sub-Specialties */}
                                        {isSelected && isExpanded && (
                                            <div className="px-4 pb-3 pt-1 bg-emerald-50 border-t border-emerald-100">
                                                <p className="text-xs text-gray-500 mb-2">Optional — narrow down what you need:</p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {spec.subSpecialties.map(sub => (
                                                        <button
                                                            key={sub}
                                                            type="button"
                                                            onClick={() => toggleSubSpecialty(sub)}
                                                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition
                                                                ${formData.sub_specialties_needed.includes(sub)
                                                                ? 'bg-emerald-600 text-white border-emerald-600'
                                                                : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400'}`}
                                                        >
                                                            {sub}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {formData.specialties_needed.length > 0 && (
                            <p className="text-xs text-emerald-600 mt-2 font-medium">
                                {formData.specialties_needed.length} specialt{formData.specialties_needed.length === 1 ? 'y' : 'ies'} selected
                                {formData.sub_specialties_needed.length > 0 && `, ${formData.sub_specialties_needed.length} sub-specialties`}
                            </p>
                        )}
                    </div>

                    {/* ── Section: Case Types ──────────────────── */}
                    <div className="p-6 md:p-8 border-b border-gray-100">
                        <h2 className="text-lg font-bold text-gray-900 mb-1">Case Types You Handle</h2>
                        <p className="text-sm text-gray-500 mb-4">Select all that apply.</p>

                        <div className="flex flex-wrap gap-2">
                            {CASE_TYPES.map(ct => (
                                <button
                                    key={ct}
                                    type="button"
                                    onClick={() => toggleArrayField('case_types', ct)}
                                    className={`px-4 py-2 rounded-full text-sm font-medium border transition
                                        ${formData.case_types.includes(ct)
                                        ? 'bg-blue-900 text-white border-blue-900'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}
                                >
                                    {ct}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ── Section: Describe Your Case ──────────── */}
                    <div className="p-6 md:p-8 border-b border-gray-100">
                        <h2 className="text-lg font-bold text-gray-900 mb-1">Describe Your Case</h2>
                        <p className="text-sm text-gray-500 mb-4">
                            Tell us about the case you need expert help with. The more detail you provide,
                            the better we can match you with the right expert.
                        </p>

                        <textarea
                            value={formData.case_description}
                            onChange={e => setFormData({ ...formData, case_description: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                            rows={5}
                            placeholder="e.g. Medical malpractice case involving a delayed diagnosis of appendicitis in a 45-year-old patient. Looking for a general surgeon or emergency medicine physician who can review records and provide testimony on standard of care..."
                        />
                    </div>

                    {/* ── Section: Details ──────────────────────── */}
                    <div className="p-6 md:p-8 border-b border-gray-100">
                        <h2 className="text-lg font-bold text-gray-900 mb-1">A Few More Details</h2>
                        <p className="text-sm text-gray-500 mb-5">Optional — helps us prioritize.</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    How Urgently Do You Need Experts?
                                </label>
                                <select
                                    value={formData.urgency}
                                    onChange={e => setFormData({ ...formData, urgency: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                >
                                    <option value="immediate">Immediately — active case now</option>
                                    <option value="within_30_days">Within 30 days</option>
                                    <option value="within_90_days">Within 90 days</option>
                                    <option value="no_rush">No rush — just want access when ready</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Estimated Cases/Year Needing Experts
                                </label>
                                <input
                                    type="number"
                                    value={formData.estimated_cases_per_year}
                                    onChange={e => setFormData({ ...formData, estimated_cases_per_year: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    placeholder="e.g. 10"
                                    min="0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Budget Range per Expert
                                </label>
                                <select
                                    value={formData.budget_range}
                                    onChange={e => setFormData({ ...formData, budget_range: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                >
                                    <option value="">Select...</option>
                                    <option value="under_250">Under $250/hr</option>
                                    <option value="250_500">$250–$500/hr</option>
                                    <option value="500_750">$500–$750/hr</option>
                                    <option value="750_plus">$750+/hr</option>
                                    <option value="flexible">Flexible / depends on case</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Anything Else We Should Know?
                                </label>
                                <textarea
                                    value={formData.additional_notes}
                                    onChange={e => setFormData({ ...formData, additional_notes: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    rows={3}
                                    placeholder="Specific requirements, geographic needs, language needs, etc."
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Error + Submit ────────────────────────── */}
                    <div className="p-6 md:p-8" style={{ background: '#f8fafb' }}>
                        {error && (
                            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3.5 rounded-lg text-white font-bold text-lg transition-all disabled:opacity-50"
                            style={{
                                background: loading
                                    ? '#9ca3af'
                                    : 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
                                boxShadow: loading ? 'none' : '0 4px 14px rgba(5, 150, 105, 0.4)'
                            }}
                        >
                            {loading ? 'Submitting...' : 'Join the Waitlist →'}
                        </button>

                        <p className="text-center text-xs text-gray-500 mt-4">
                            No account required. No payment needed. We'll email you when your experts are ready.
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
}
